"use client"

import React, { useState, useEffect, useCallback, useRef } from "react";
import { BarChart, Bar, XAxis, CartesianGrid, ResponsiveContainer, Tooltip, Cell } from "recharts";
import { CircleHelp, Download, ChevronDown } from "lucide-react";
import { Tooltip as UITooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import axiosInstance from "@/api/axios";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useTranslation } from "react-i18next";

// Skeleton bar heights for 24 hours — a quiet overnight dip and a taller
// "peak hour" bar, shaped like a real usage day, not real data.
const SKELETON_HOURS = [
    8, 6, 5, 4, 4, 6, 12, 25, 45, 60, 70, 65,
    55, 72, 80, 68, 50, 40, 35, 28, 22, 18, 14, 10,
];

export function PeakUsageTime({ 
    className, 
    title, 
    timeRange = 'Year', 
    bins = [], 
    customDateRange = [{ startDate: null, endDate: null, key: 'selection' }],
    isCustomDateRange = false,
    ...props 
}) {
    const { t } = useTranslation();
    const [activityData, setActivityData] = useState({});
    const [loading, setLoading] = useState(true);
    const [selectedDay, setSelectedDay] = useState(0);
    const [currentDay, setCurrentDay] = useState(0);
    const [error, setError] = useState(null);
    const exportRef = useRef(null);

    const daysOfWeek = [
        t("monday"),
        t("tuesday"),
        t("wednesday"),
        t("thursday"),
        t("friday"),
        t("saturday"),
        t("sunday")
    ];

    const getActivityDescription = useCallback((activityLevel) => {
        const descriptions = {
            0: t("notBusy"),
            1: t("notTooBusy"),
            2: t("aLittleBusy"),
            3: t("usuallyBusy"),
            4: t("usuallyVeryBusy")
        };
        return descriptions[activityLevel] || t("notBusy");
    }, [t]);

    const generateMockData = useCallback(() => {
        const data = {};
        for (let day = 0; day < 7; day++) {
            data[day] = {};
            for (let hour = 0; hour < 24; hour++) {
                let count = 0;
                if (hour >= 8 && hour <= 18 && day >= 0 && day <= 4) {
                    count = Math.floor(Math.random() * 15) + 1;
                } else if ((hour >= 10 && hour <= 16) && (day === 5 || day === 6)) {
                    count = Math.floor(Math.random() * 10) + 1;
                } else if (hour >= 22 || hour <= 5) {
                    count = Math.floor(Math.random() * 2);
                } else {
                    count = Math.floor(Math.random() * 5);
                }
                data[day][hour] = count;
            }
        }
        return data;
    }, []);

    const prepareChartData = useCallback(() => {
        if (!activityData || !activityData[selectedDay]) {
            return [];
        }

        const dayData = activityData[selectedDay];
        const maxCount = Math.max(...Object.values(dayData));

        return Object.keys(dayData).map(hour => {
            const hourNum = parseInt(hour);
            const formattedHour = hourNum % 12 || 12;
            const amPm = hourNum < 12 ? "a" : "p";
            const count = dayData[hour];

            let activityLevel = 0;
            if (maxCount > 0) {
                const ratio = count / maxCount;
                if (ratio === 0) activityLevel = 0;
                else if (ratio <= 0.25) activityLevel = 1;
                else if (ratio <= 0.5) activityLevel = 2;
                else if (ratio <= 0.75) activityLevel = 3;
                else activityLevel = 4;
            }

            return {
                hour: `${formattedHour}${amPm}`,
                count: count,
                fullHour: hour,
                activityLevel: activityLevel
            };
        });
    }, [activityData, selectedDay]);

    const getExportData = useCallback(() => {
        if (!activityData || !activityData[selectedDay]) return null;

        const dayData = activityData[selectedDay];
        const maxCount = Math.max(...Object.values(dayData));

        const headers = [t("hour"), t("wasteItems"), t("activityLevel"), "Is Peak Hour"];
        const rows = Object.entries(dayData).map(([hour, count]) => {
            const hourNum = parseInt(hour);
            const formattedHour = hourNum % 12 || 12;
            const amPm = hourNum < 12 ? "AM" : "PM";
            const isBusiest = count === maxCount;

            let activityLevel = 0;
            if (maxCount > 0) {
                const ratio = count / maxCount;
                if (ratio === 0) activityLevel = 0;
                else if (ratio <= 0.25) activityLevel = 1;
                else if (ratio <= 0.5) activityLevel = 2;
                else if (ratio <= 0.75) activityLevel = 3;
                else activityLevel = 4;
            }

            return [
                `${formattedHour} ${amPm}`,
                count.toString(),
                getActivityDescription(activityLevel),
                isBusiest ? "Yes" : "No"
            ];
        });

        return {
            title: `${title || t("peakUsageTime")} - ${daysOfWeek[selectedDay]}`,
            headers,
            rows,
            chartData: prepareChartData()
        };
    }, [activityData, selectedDay, title, daysOfWeek, getActivityDescription, prepareChartData, t]);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            setError(null);

            try {
                const params = {
                    bins: bins.join(",")
                };

                // Add custom date range if active - this takes priority over timeRange
                // SAFE DATE HANDLING APPLIED HERE
                if (isCustomDateRange && customDateRange[0] && customDateRange[0].startDate && customDateRange[0].endDate) {
                    const startDate = customDateRange[0].startDate;
                    const endDate = customDateRange[0].endDate;
                    
                    if (startDate instanceof Date) {
                        params.start_date = startDate.toISOString();
                    } else if (typeof startDate === 'string') {
                        params.start_date = startDate;
                    } else {
                        params.start_date = new Date(startDate).toISOString();
                    }

                    if (endDate instanceof Date) {
                        params.end_date = endDate.toISOString();
                    } else if (typeof endDate === 'string') {
                        params.end_date = endDate;
                    } else {
                        params.end_date = new Date(endDate).toISOString();
                    }
                    
                    console.log('PeakUsageTime: Using custom date range', params.start_date, params.end_date);
                } else {
                    // Only use timeRange if NOT using custom date range
                    params.timeRange = timeRange;
                    console.log('PeakUsageTime: Using time range', timeRange);
                }

                console.log('PeakUsageTime: API params', params);

                const response = await axiosInstance.get("peak_usage_time", {
                    params: params,
                    paramsSerializer: {
                        indexes: null,
                    },
                });

                console.log('PeakUsageTime: API response', response.data);

                if (response.data) {
                    const usageData = response.data.usage_data || {};
                    console.log('PeakUsageTime: Usage data', usageData);
                    
                    // Check if we have any data
                    const hasAnyData = Object.keys(usageData).some(day => 
                        Object.values(usageData[day] || {}).some(count => count > 0)
                    );
                    
                    console.log('PeakUsageTime: Has any data?', hasAnyData);
                    
                    setActivityData(usageData);
                    setCurrentDay(response.data.current_day || 0);
                    setSelectedDay(response.data.current_day || 0);
                }
            } catch (err) {
                console.error('Error fetching peak usage data:', err);
                console.error('Error response:', err.response?.data);
                setError(t("failedToLoadUsageData"));
                setActivityData(generateMockData());
                const today = new Date().getDay();
                const currentDayAdjusted = today === 0 ? 6 : today - 1;
                setCurrentDay(currentDayAdjusted);
                setSelectedDay(currentDayAdjusted);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [timeRange, bins, customDateRange, isCustomDateRange, generateMockData, t]);

    const getColorByActivity = (activityLevel, isBusiest = false) => {
        if (isBusiest) {
            return "hsl(var(--bright-red))";
        }
        return "hsl(var(--chart-6))";
    };

    const getCurrentStatus = () => {
        if (!activityData || !activityData[selectedDay]) {
            return {isLive: false, status: t("notBusy"), busiestHour: null, activityLevel: 0};
        }

        const currentHour = new Date().getHours();
        const dayData = activityData[selectedDay];
        const maxCount = Math.max(...Object.values(dayData));
        const currentCount = dayData[currentHour] || 0;

        let currentActivityLevel = 0;
        if (maxCount > 0) {
            const ratio = currentCount / maxCount;
            if (ratio === 0) currentActivityLevel = 0;
            else if (ratio <= 0.25) currentActivityLevel = 1;
            else if (ratio <= 0.5) currentActivityLevel = 2;
            else if (ratio <= 0.75) currentActivityLevel = 3;
            else currentActivityLevel = 4;
        }

        let maxHour = null;
        let maxValue = 0;
        Object.entries(dayData).forEach(([hour, count]) => {
            if (count > maxValue) {
                maxValue = count;
                maxHour = hour;
            }
        });

        let formattedBusiestHour = null;
        if (maxHour !== null && maxValue > 0) {
            const hourNum = parseInt(maxHour);
            const formattedHour = hourNum % 12 || 12;
            const amPm = hourNum < 12 ? "AM" : "PM";
            formattedBusiestHour = `${formattedHour} ${amPm}`;
        }

        return {
            isLive: selectedDay === currentDay,
            status: getActivityDescription(currentActivityLevel),
            busiestHour: formattedBusiestHour,
            activityLevel: currentActivityLevel
        };
    };

    const CustomTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            const data = payload[0].payload;
            const hourValue = data.fullHour;
            const hourFormatted = parseInt(hourValue) % 12 || 12;
            const amPm = parseInt(hourValue) < 12 ? "AM" : "PM";
            const count = payload[0].value;
            const isBusiest = data.isBusiest;

            return (
                <div className="rounded-lg border bg-background p-2 shadow-sm text-sm">
                    <p className="font-medium">{`${hourFormatted} ${amPm}`}</p>
                    <p>{count === 1 ? `1 ${t("wasteItem")}` : `${count} ${t("wasteItems")}`}</p>
                    {isBusiest && <p className="text-red-600 text-xs mt-1">{t("peakHour")}</p>}
                </div>
            );
        }
        return null;
    };

    const handleExportCSV = () => {
        const exportData = getExportData();
        if (!exportData) return;

        const csvContent = [
            exportData.headers.join(","),
            ...exportData.rows.map(row => row.join(","))
        ].join("\n");

        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", `peak_usage_${daysOfWeek[selectedDay]}_${new Date().toISOString().slice(0, 10)}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleExportPNG = async () => {
        if (!exportRef.current) return;

        const originalStyles = {
            title: exportRef.current.querySelector('h4').style.visibility,
            tabs: exportRef.current.querySelector('[role="tablist"]').style.visibility,
            chart: exportRef.current.querySelector('.recharts-wrapper').style.visibility
        };

        exportRef.current.querySelector('h4').style.visibility = 'visible';
        exportRef.current.querySelector('[role="tablist"]').style.visibility = 'visible';
        exportRef.current.querySelector('.recharts-wrapper').style.visibility = 'visible';

        const exportButtons = exportRef.current.querySelectorAll('.peak-usage-export, .no-export');
        exportButtons.forEach(btn => btn.style.display = 'none');

        try {
            await new Promise(resolve => setTimeout(resolve, 50));

            const html2canvas = (await import('html2canvas')).default;
            const canvas = await html2canvas(exportRef.current, {
                scale: 2,
                logging: false,
                useCORS: true,
                backgroundColor: "white",
                windowWidth: exportRef.current.scrollWidth,
                windowHeight: exportRef.current.scrollHeight,
                ignoreElements: (element) => element.classList.contains('no-export')
            });

            const link = document.createElement('a');
            link.download = `peak_usage_${daysOfWeek[selectedDay]}_${new Date().toISOString().slice(0, 10)}.png`;
            link.href = canvas.toDataURL('image/png');
            link.click();
        } catch (err) {
            console.error('Failed to export PNG:', err);
        } finally {
            exportRef.current.querySelector('h4').style.visibility = originalStyles.title;
            exportRef.current.querySelector('[role="tablist"]').style.visibility = originalStyles.tabs;
            exportRef.current.querySelector('.recharts-wrapper').style.visibility = originalStyles.chart;
            exportButtons.forEach(btn => btn.style.display = '');
        }
    };

    const chartData = prepareChartData();
    const currentStatus = getCurrentStatus();

    if (chartData.length > 0) {
        const maxCount = Math.max(...chartData.map(item => item.count));
        chartData.forEach(item => {
            item.isBusiest = item.count === maxCount;
        });
    }

    if (loading) {
        return (
            <div className={cn("lg:col-span-2 aspect-[16/10] sm:aspect-[16/10] sm:rounded-lg bg-muted/50 p-4 exportable-section", className)} {...props}>
                <div className="flex flex-row items-center justify-between">
                    <h4 className="scroll-m-20 text-xl font-semibold tracking-tight">{title || t("peakUsageTime")}</h4>
                    <TooltipProvider>
                        <UITooltip>
                            <TooltipTrigger asChild>
                                <CircleHelp className="h-5 w-5 text-muted-foreground"/>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p>{t("showsThePeakUsageTimesForSelectedBins")}</p>
                            </TooltipContent>
                        </UITooltip>
                    </TooltipProvider>
                </div>

                {/* Skeleton — same shape as the loaded view: 7 day-tab
                    placeholders, the "busiest hour" line, and 24 bars with
                    one taller peak, matching the real bar chart. */}
                <div
                    className="flex flex-col h-[320px] pt-2 motion-safe:animate-pulse"
                    aria-busy="true"
                >
                    <span className="sr-only" role="status">{t("loadingUsageData", "Loading…")}</span>

                    <div className="mb-3">
                        <div className="w-full grid grid-cols-7 gap-1 p-1 bg-muted rounded-md">
                            {[0, 1, 2, 3, 4, 5, 6].map((i) => (
                                <div key={i} className="h-6 rounded-sm bg-gray-200 dark:bg-slate-700" />
                            ))}
                        </div>

                        <div className="flex items-center mt-3 gap-2">
                            <div className="h-4 w-4 rounded-full bg-gray-200 dark:bg-slate-700" />
                            <div className="h-3 w-14 rounded bg-gray-200 dark:bg-slate-700" />
                            <div className="h-3 w-24 rounded bg-gray-200 dark:bg-slate-700" />
                        </div>
                    </div>

                    <div className="flex-grow flex items-end gap-1 px-1 pb-5">
                        {SKELETON_HOURS.map((h, i) => (
                            <div
                                key={i}
                                className="flex-1 rounded-t bg-gray-200 dark:bg-slate-700"
                                style={{ height: `${h}%` }}
                            />
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={cn("lg:col-span-2 aspect-[16/10] sm:aspect-[16/10] sm:rounded-lg bg-muted/50 p-4", className)} {...props}>
                <div className="flex flex-row items-center justify-between">
                    <h4 className="scroll-m-20 text-xl font-semibold tracking-tight">{title || t("peakUsageTime")}</h4>
                    <TooltipProvider>
                        <UITooltip>
                            <TooltipTrigger asChild>
                                <CircleHelp className="h-5 w-5 text-muted-foreground"/>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p>{t("showsThePeakUsageTimesForSelectedBins")}</p>
                            </TooltipContent>
                        </UITooltip>
                    </TooltipProvider>
                </div>
                <div className="flex items-center justify-center h-[320px] text-red-500">
                    <p>{error}</p>
                </div>
            </div>
        );
    }

    return (
        <div
            ref={exportRef}
            className={cn("exportable-section lg:col-span-2 aspect-[1/1] sm:aspect-[16/10] sm:rounded-lg bg-muted/50 p-4", className)}
            data-export-data={JSON.stringify(getExportData())}
            {...props}
        >
            <div className="relative">
                <div className="flex justify-between items-center mb-2">
                    <div>
                        <h4 className="scroll-m-20 text-xl font-semibold tracking-tight">
                            {title || t("peakUsageTime")}
                        </h4>
                        {isCustomDateRange && customDateRange[0]?.startDate && customDateRange[0]?.endDate && (
                            <p className="text-sm text-muted-foreground mt-1">
                                {(() => {
                                    const sDate = customDateRange[0].startDate instanceof Date ? customDateRange[0].startDate : new Date(customDateRange[0].startDate);
                                    const eDate = customDateRange[0].endDate instanceof Date ? customDateRange[0].endDate : new Date(customDateRange[0].endDate);
                                    
                                    if (!isNaN(sDate) && !isNaN(eDate)) {
                                        return `${sDate.toLocaleDateString()} - ${eDate.toLocaleDateString()}`;
                                    }
                                    return '';
                                })()}
                            </p>
                        )}
                    </div>

                    <div className="flex items-center space-x-2 export-controls peak-usage-export no-export">
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-muted-foreground hover:text-primary"
                                    disabled={!activityData || !activityData[selectedDay]}
                                >
                                    <Download className="h-4 w-4 mr-1"/>
                                    {t("export")}
                                    <ChevronDown className="h-4 w-4 ml-1"/>
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                                <DropdownMenuItem
                                    onClick={handleExportCSV}
                                    disabled={!activityData || !activityData[selectedDay]}
                                >
                                    {t("downloadAsCSV")}
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                    onClick={handleExportPNG}
                                    disabled={!activityData || !activityData[selectedDay]}
                                >
                                    {t("downloadAsPNG")}
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>

                        <TooltipProvider>
                            <UITooltip>
                                <TooltipTrigger asChild>
                                    <CircleHelp className="h-5 w-5 text-muted-foreground" />
                                </TooltipTrigger>
                                <TooltipContent>
                                    <p>{t("showsThePeakUsageTimesForSelectedBins")}<br/><br/><strong>Date Range:</strong> Use the calendar in the header to select a custom date range</p>
                                </TooltipContent>
                            </UITooltip>
                        </TooltipProvider>
                    </div>
                </div>
            </div>

            <div className="px-2 pt-4 sm:px-6 sm:pt-6" style={{ width: '100%', height: '100%' }}>
                {!activityData || !activityData[selectedDay] ? (
                    <div className="flex h-full w-full items-center justify-center text-muted-foreground">
                        {t("noUsageDataAvailable")}
                    </div>
                ) : (
                    <div className="flex flex-col h-full">
                        <div className="mb-3">
                            <Tabs
                                defaultValue={currentDay.toString()}
                                value={selectedDay.toString()}
                                onValueChange={(value) => setSelectedDay(parseInt(value))}
                                className="w-full mb-3"
                            >
                                <TabsList className="w-full grid grid-cols-7 gap-1 p-1 bg-muted rounded-md">
                                    {daysOfWeek.map((day, index) => (
                                        <TabsTrigger
                                            key={index}
                                            value={index.toString()}
                                            className={cn(
                                                "py-1 text-xs sm:text-sm font-medium relative transition-all",
                                                "data-[state=active]:bg-background data-[state=active]:text-foreground",
                                                "data-[state=active]:shadow-sm rounded-sm",
                                                "text-muted-foreground hover:text-foreground",
                                                "flex justify-center"
                                            )}
                                        >
                                            <span className="inline-flex items-center">
                                                {day.substring(0, 3)}
                                                {index === currentDay && (
                                                    <span className={cn(
                                                        "ml-1 h-1.5 w-1.5 rounded-full",
                                                        selectedDay === index ? "bg-primary" : "bg-muted-foreground"
                                                    )}></span>
                                                )}
                                            </span>
                                        </TabsTrigger>
                                    ))}
                                </TabsList>
                            </Tabs>

                            {currentStatus.busiestHour && (
                                <div className="flex items-center mt-2">
                                    <span className="inline-flex mr-2">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-blue-500"
                                           viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd"
                                              d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v3.586L7.707 9.293a1 1 0 00-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 10.586V7z"
                                              clipRule="evenodd"/>
                                        </svg>
                                    </span>
                                    <span className="font-medium text-blue-500 text-sm">
                                        {currentStatus.busiestHour}:
                                    </span>
                                    <span className="ml-2 text-sm text-muted-foreground">{t("usuallyBusiest")}</span>
                                </div>
                            )}
                        </div>

                        <div className="flex-grow w-full">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart
                                    data={chartData}
                                    margin={{top: 5, right: 5, left: 5, bottom: 20}}
                                >
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--muted-foreground)/0.2)"/>
                                    <XAxis
                                        dataKey="hour"
                                        tick={{fontSize: 12, fill: "hsl(var(--muted-foreground))"}}
                                        axisLine={false}
                                        tickLine={false}
                                    />
                                    <Tooltip content={<CustomTooltip/>}/>
                                    <Bar
                                        dataKey="count"
                                        radius={[2, 2, 0, 0]}
                                        maxBarSize={16}
                                    >
                                        {chartData.map((entry, index) => (
                                            <Cell
                                                key={`cell-${index}`}
                                                fill={getColorByActivity(entry.activityLevel, entry.isBusiest)}
                                            />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}